import React from 'react';
import { ExternalLink, Building2, Gavel, Landmark, Map, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { LegalNewsItem } from '../../redux/slices/legalNewsSlice';
import { cn } from '@/lib/utils';

interface NewsItemProps {
  item: LegalNewsItem;
}

interface SourceConfig {
  icon: React.ReactNode;
  badgeVariant: 'default' | 'secondary' | 'outline';
  accentClass: string;
}

const getSourceConfig = (source: string): SourceConfig => {
  switch (source) {
    case 'cyprus_bar':
      return {
        icon: <Gavel size={12} className="shrink-0" />,
        badgeVariant: 'default',
        accentClass: 'border-l-primary',
      };
    case 'ministry_of_justice':
      return {
        icon: <Landmark size={12} className="shrink-0" />,
        badgeVariant: 'secondary',
        accentClass: 'border-l-violet-500',
      };
    case 'land_registry':
      return {
        icon: <Map size={12} className="shrink-0" />,
        badgeVariant: 'outline',
        accentClass: 'border-l-emerald-500',
      };
    case 'cylaw':
      return {
        icon: <Building2 size={12} className="shrink-0" />,
        badgeVariant: 'outline',
        accentClass: 'border-l-sky-500',
      };
    default:
      return {
        icon: <Building2 size={12} className="shrink-0" />,
        badgeVariant: 'outline',
        accentClass: 'border-l-muted-foreground',
      };
  }
};

const NewsItem: React.FC<NewsItemProps> = ({ item }) => {
  const { t } = useTranslation();
  const config = getSourceConfig(item.source);

  let relativeTime = '';
  try {
    relativeTime = formatDistanceToNow(new Date(item.publishedAt), { addSuffix: true });
  } catch (e) {
    relativeTime = item.publishedAt;
  }

  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'group block rounded-lg border bg-card text-card-foreground shadow-sm',
        'border-l-4 p-4',
        'hover:shadow-md hover:bg-accent/30 transition-all duration-200',
        config.accentClass,
      )}
    >
      {/* Top row: source badge + time */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <Badge
          variant={config.badgeVariant}
          className="flex items-center gap-1 text-[11px] font-medium px-2 py-0.5"
        >
          {config.icon}
          <span>{item.sourceLabel}</span>
        </Badge>
        <div className="flex items-center gap-1 text-muted-foreground text-[11px] shrink-0">
          <Clock size={11} />
          <span>{relativeTime}</span>
        </div>
      </div>

      {/* Title */}
      <h3 className="text-sm font-semibold text-foreground leading-snug mb-1.5 line-clamp-2 group-hover:text-gold transition-colors">
        {item.title}
      </h3>

      {/* Summary */}
      {item.summary && (
        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-3 mb-2">
          {item.summary}
        </p>
      )}

      {/* Read more link */}
      <div className="flex items-center gap-1 text-xs font-medium text-gold opacity-0 group-hover:opacity-100 transition-opacity">
        <span>{t('legalNews.readOriginal', { defaultValue: 'Read original' })}</span>
        <ExternalLink size={11} />
      </div>
    </a>
  );
};

export default React.memo(NewsItem);
